import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Box, Button, IconButton, Tooltip, useTheme, Alert } from "@mui/material";
import { Add } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useUsers } from "../../hooks/useUsers";
import ModalDialog from "../../components/ModalDialog";
import { getLocalDateTime } from "../../utilities/helpers";
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import RoleFormModal from "./RoleFormModal";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { TENANT_MASTER } from "../../utilities/constants";

const Users = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [roleModal, setRoleModal] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const { users, managers, roleList, isLoading, fetchUsers, deleteUser } = useUsers();
  
  const tenantId = useSelector((state) => state.tenantId);
  const idToken = useSelector((state) => state.token);

  const fetchTenantInfo = async () => {
    if (tenantId && tenantId !== TENANT_MASTER) {
      try {
        const requestHeader = {
          headers: {
            "x-eqip-tenantid": tenantId,
            Authorization: idToken,
          },
        };
        const response = await ieqipCoreServiceApi.get(`/api/tenants/${tenantId}`, requestHeader);
        setTenantInfo(response.data);
      } catch (error) {
        console.error("Error fetching tenant info:", error);
      }
    }
  };

  useEffect(() => {
    fetchTenantInfo();
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      field: "Username",
      headerName: "Name",
      flex: 1,
    },
    {
      headerName: "Role",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <>{row.role?.description}</>
        );
      },
    },
    {
      field: "tenant_name",
      headerName: "Company/Tenant",
      flex: 1,
    },
    {
      field: "UserStatus",
      headerName: "Status",
      flex: 1,
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
    },
    {
      field: "UserCreateDate",
      headerName: "Created Date",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <>{getLocalDateTime(row.UserCreateDate)}</>
        );
      },
    },
    {
      field: "Enabled",
      headerName: "Enable",
      flex: 1,
    },
    {
      field: "id",
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box>
            <Tooltip title="Set Role">
              <IconButton color="warning" onClick={() => handleSetRole(row)} variant="" disabled={row?.tenant_name.includes('eQip') && row.Username?.includes('khiem')}>
                <SupervisedUserCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleDelete(row)} variant="" disabled={row?.tenant_name === 'ieQip' && row.Username?.includes('khiem')}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    
  ];

  const handleDeleteOk = async () => {
    setOpenDeleteModal(false);
    await deleteUser(selectedUser);
  }

  const handleDeleteCancel = () => {
    setOpenDeleteModal(false);
  }

  const handleDelete = (user) => {
    setOpenDeleteModal(true);
    setSelectedUser(user)
  }

  const handleSetRole = async (user) => {
    setRoleModal(true);
    setSelectedUser(user)
  }

  const handleSetRoleOk = async () => {
    setRoleModal(false);
  }

  const handleSetRoleCancel = async () => {
    setRoleModal(false);
  }

  return (
    <Box m="20px">
      <Header title="USER" subtitle="Managing the Users" />
      {tenantInfo?.has_limit && (
        <Alert 
          severity={tenantInfo.limit_reached ? "error" : "info"} 
          sx={{ mb: 2 }}
        >
          {tenantInfo.limit_reached 
            ? `User limit reached: ${tenantInfo.current_user_count} / ${tenantInfo.max_users} users. Cannot create new users.`
            : `User limit: ${tenantInfo.current_user_count} / ${tenantInfo.max_users} users`
          }
        </Alert>
      )}
      <Box display="flex" justifyContent="end">
        <Tooltip title={tenantInfo?.limit_reached ? "User limit reached for this tenant" : ""}>
          <span>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              component={Link}
              to="/users/create"
              disabled={tenantInfo?.limit_reached}
            >
              Create User
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.blueAccent[600],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.grey[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.grey[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.grey[500]} !important`,
          },
        }}
      >
        <DataGrid
          rows={users}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
        />
      </Box>
      <ModalDialog
        title="Delete"
        content="Are you sure you want to delete this item?"
        open={openDeleteModal}
        handleOk={handleDeleteOk}
        handleCancel={handleDeleteCancel} />

      <ModalDialog
        title="Set Role"
        content={<RoleFormModal 
          roleList={roleList} 
          managers={managers} 
          selectedUser={selectedUser} 
          setRoleModal={setRoleModal} 
          fetchUsers={fetchUsers}/>}
        open={roleModal}
        handleOk={handleSetRoleOk}
        handleCancel={handleSetRoleCancel}
        hideOK={true}
        hideCancel={true}
      />
    </Box>
  );
};

export default Users;
